/**
 * Applies the person's own appearance choices (spec §5 Ko'rinish, D9.110) to the document.
 *
 * The theme is a `data-theme` attribute on `<html>`, which is what `tokens.css` keys the dark
 * palette on. "Qurilmaga qarab" follows the device's preference, and only then: an agency's
 * screens stay light unless a person chose otherwise. The language is remembered on the
 * device and the page reloads once when it changes, because strings already on screen are
 * not re-rendered.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { profileApi, type Theme } from "@/api/profile";
import { queryKeys } from "@/api/queryKeys";
import { setLocale } from "@/i18n";
import { setMoneyDisplay } from "@/lib/format";
import { useAuthStore } from "@/store/auth";

/** The palette a theme choice resolves to, given what the device prefers. Pure. */
export function resolveTheme(theme: Theme, devicePrefersDark: boolean): "light" | "dark" {
  if (theme === "system") return devicePrefersDark ? "dark" : "light";
  return theme;
}

function applyTheme(theme: Theme): () => void {
  const root = document.documentElement;
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const apply = (): void => {
    root.dataset.theme = resolveTheme(theme, query.matches);
  };
  apply();
  if (theme !== "system") return () => undefined;
  query.addEventListener("change", apply);
  return () => {
    query.removeEventListener("change", apply);
  };
}

/** Reads the person's preferences once signed in to an agency, and applies them. */
export function useApplyPreferences(): void {
  const signedIn = useAuthStore((state) => state.status === "authenticated" && state.user?.tenant != null);
  const { data } = useQuery({
    queryKey: queryKeys.profile.preferences(),
    queryFn: profileApi.preferences,
    enabled: signedIn,
    staleTime: 5 * 60 * 1000,
  });
  const theme = data?.theme;
  const language = data?.language;

  useEffect(() => {
    if (theme === undefined) return undefined;
    return applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (data?.currency_display !== undefined) setMoneyDisplay(data.currency_display);
  }, [data?.currency_display]);

  useEffect(() => {
    if (language === undefined) return;
    if (setLocale(language)) window.location.reload();
  }, [language]);
}
